import { LitElement, html } from 'lit';
import { customElement, state, query, property } from 'lit/decorators.js';

import { emit } from '@amityeko/pbc-core/dist/utilities/event';
import { withAuth } from '@amityeko/pbc-core/dist/providers/auth/consumer-mixin';

import closeIcon from '@amityeko/design-system/dist/assets/icons/close';

import '@amityeko/design-system/dist/elements/chip';
import '@amityeko/design-system/dist/elements/dialog';
import '@amityeko/design-system/dist/elements/button';
import type AmityekoDialog from '@amityeko/design-system/dist/elements/dialog';

import '../recognition-preview';
import { RecognitionDetail } from '../recoginition-list-per-user/types';

import { Badge } from './types';

import styles from './styles';

@customElement('rnr-view-recognition-message')
export default class RnRViewRecognitionMessage extends withAuth(LitElement) {
  static styles = styles;

  @query('#dialog') dialog!: AmityekoDialog;

  @property({ type: Number })
  previewScale?: number;

  @state()
  recognitionDetail?: RecognitionDetail;

  @state()
  animation?: string;

  @state()
  badge?: Badge;

  @state()
  backgroundImage?: string;

  @state()
  images?: any;

  @state()
  videoURL?: string;

  @state()
  isClickedViewDetailBtn: boolean = false;

  async show(recognitionDetail: any = {}) {
    this.dialog.show();

    if (Object.keys(recognitionDetail).length > 0) {
      this.recognitionDetail = recognitionDetail;
      try {
        if (!this.recognitionDetail?.badge) return;

        // TODO: need to review types
        let badge: any = this.recognitionDetail.badge as unknown as Badge;

        if (!badge?.id) return;

        // We have two shapes of the recognition of incoming data:
        // 1) In one, the badge has complete data (we check it by animation property).
        // 2) In other - it has only id. So if the animation field is not available
        //    - query for complete data.
        if (!badge?.animation) {
          badge = await this.auth?.http?.get(`/api/v1/badges/${badge.id}`);
        }

        // Call the animation api
        if (badge?.animation) {
          const animationSrc = await this.auth?.http?.media(
            `/api/v1/media/images/${badge.animation}`,
          );
          this.animation = animationSrc;
        }

        let badgeImage;
        if (badge?.thumbnail) {
          badgeImage = await this.auth?.http?.media(`/api/v1/media/images/${badge.thumbnail}`);
        }

        this.badge = {
          name: badge?.name,
          point: recognitionDetail?.rewards[0]?.value,
          src: badgeImage,
        };

        // Call the background image api
        if (this.recognitionDetail?.background?.mediaId) {
          const backgroundImage = await this.auth?.http?.media(
            `/api/v1/media/images/${this.recognitionDetail?.background?.mediaId}`,
          );
          this.backgroundImage = backgroundImage;
        } else {
          this.backgroundImage = undefined;
        }

        //fetch images
        if (this.recognitionDetail?.media && this.recognitionDetail?.media[0]?.type === 'image') {
          const imageKeys = this.recognitionDetail?.media?.map(media => ({
            type: media.type,
            key: media?.data?.image?.mediaId || '',
          }));

          this.images = {
            layout: this.recognitionDetail?.imageLayout,
            imageCount: this.recognitionDetail?.media?.length || 1,
            imageKeys: imageKeys,
          };
        } else {
          this.images = undefined;
        }

        //fetch video
        if (this.recognitionDetail?.media && this.recognitionDetail?.media[0]?.type === 'video') {
          const videoId = this.recognitionDetail?.media[0]?.data?.video?.mediaId;
          const videoURL = await this.auth?.http?.media(`/api/v1/media/videos/${videoId}`);
          this.videoURL = videoURL;
        } else {
          this.videoURL = undefined;
        }
      } catch (e: any) {
        emit(this, 'amityeko-notification--toast', {
          detail: { message: e?.error?.message, variant: 'error' },
        });
      }
    }
  }

  private _onTriggerClick() {
    this.dialog.show();
  }

  private _onViewDetailDialogHide() {
    if (this.isClickedViewDetailBtn) {
      emit(this, 'rnr-on-view-detail-hide', {
        detail: {
          recognitionId: this.recognitionDetail?.id,
        },
      });
    } else {
      emit(this, 'rnr-on-view-detail-hide');
    }

    this.isClickedViewDetailBtn = false;
  }

  private _onClose() {
    this.dialog.hide();
  }

  private _onViewDetailClick() {
    this.isClickedViewDetailBtn = true;
    this.dialog.hide();
  }

  protected render() {
    let gifId;
    if (
      this.recognitionDetail?.media?.length &&
      this.recognitionDetail?.media[0]?.type === 'giphy'
    ) {
      gifId = this.recognitionDetail!.media[0]!.data!.giphy!.giphyId;
    }
    return html`
      <slot @click=${this._onTriggerClick}></slot>

      <amityeko-dialog
        id="dialog"
        variant="pure"
        @amityeko-after-hide=${this._onViewDetailDialogHide}
      >
        <rnr-recognition-preview
          .scale=${this.previewScale}
          .badgeSrc=${this.badge?.src || ''}
          .badgeName=${this.badge?.name}
          .badgePoint=${this.badge?.point}
          .bgFromColor=${this.recognitionDetail?.background?.colors[0]}
          .bgToColor=${this.recognitionDetail?.background?.colors[1]}
          .bgImage=${this.backgroundImage}
          .animation=${this.animation || ''}
          .textColor=${this.recognitionDetail?.message?.color}
          .text=${this.recognitionDetail?.message?.data}
          .gifId=${gifId}
          .images=${this.images}
          .videoURL=${this.videoURL}
          .videoTitle=${this.recognitionDetail?.message?.data}
          .videoTitleColor=${this.recognitionDetail?.message?.color}
          ?isTitleEmbedded=${this.recognitionDetail?.meta?.video?.isTitleEmbedded}
        ></rnr-recognition-preview>
        <div class="recognition-buttons">
          <amityeko-chip
            clickable
            selectionColor="#424242"
            @amityeko-click-chip=${this._onViewDetailClick}
          >
            View details
          </amityeko-chip>
          <amityeko-button circle @click=${this._onClose}>
            <amityeko-icon .iconSize=${'12px'}>${closeIcon}</amityeko-icon>
          </amityeko-button>
        </div>
      </amityeko-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rnr-view-recognition-message': RnRViewRecognitionMessage;
  }
}
